import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  TextField,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Alert,
  InputAdornment,
  Chip,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import { useEffect, useMemo, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import * as services from "../../../services/customer";

const QrMatchingDialog = ({
  open,
  onClose,
  onSelect,      // (uid) => void  -> เพิ่ม
  onRemove,      // (uid) => void  -> ลบ
  excludedUids = [], // รายการที่ "เลือกแล้ว" จาก parent
}) => {
  const [search, setSearch] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerError, setScannerError] = useState("");
  const qrScannerRef = useRef(null);
  const scannerRegionId = "matching-qr-reader";

  // set ของ uid ที่ถูกเลือกแล้ว
  const selectedSet = useMemo(
    () => new Set(excludedUids),
    [excludedUids]
  );

  // 🔎 SEARCH
  useEffect(() => {
    if (!open) return;

    const delay = setTimeout(async () => {
      try {
        setLoading(true);
        setError("");

        const res = await services.getQrcode(search || undefined);

        const data = (res?.data?.data || []).map((item) => ({
          id: item.id,
          value: item.uid,
          createdAt: item.createdAt,
        }));

        setList(data);
      } catch (err) {
        setError("โหลด QR ไม่สำเร็จ");
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(delay);
  }, [search, open]);

  // 🛑 STOP SCANNER
  const stopScanner = async () => {
    try {
      if (qrScannerRef.current) {
        await qrScannerRef.current.stop();
        await qrScannerRef.current.clear();
        qrScannerRef.current = null;
      }
    } catch {}
  };

  // 📷 START SCANNER
  useEffect(() => {
    if (!scannerOpen) {
      stopScanner();
      return;
    }

    const start = async () => {
      try {
        const scanner = new Html5Qrcode(scannerRegionId);
        qrScannerRef.current = scanner;

        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: 220 },
          (decoded) => {
            handleToggle(decoded);
          }
        );
      } catch {
        setScannerError("เปิดกล้องไม่ได้");
      }
    };

    setTimeout(start, 150);

    return stopScanner;
  }, [scannerOpen, selectedSet]);

  // 🔥 TOGGLE LOGIC (สำคัญ)
  const handleToggle = (uid) => {
    if (selectedSet.has(uid)) {
      onRemove?.(uid); // 👈 ลบ
    } else {
      onSelect?.(uid); // 👈 เพิ่ม
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          เลือก QR
          <IconButton onClick={onClose} sx={{ float: "right" }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {error && <Alert severity="error">{error}</Alert>}

          <Stack spacing={2} mb={2}>
            <TextField
              label="ค้นหา UID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setScannerOpen(true)}>
                      <QrCodeScannerIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Typography variant="caption">
              {loading ? "กำลังโหลด..." : `${list.length} รายการ`}
            </Typography>
          </Stack>

          <Box maxHeight={320} overflow="auto">
            <List dense>
              {list.map((qr) => {
                const selected = selectedSet.has(qr.value);

                return (
                  <ListItemButton
                    key={qr.id}
                    onClick={() => handleToggle(qr.value)}
                  >
                    <ListItemText
                      primary={
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Box display="flex" gap={1} alignItems="center">
                            <Typography fontWeight={600}>
                              {qr.value}
                            </Typography>

                            {selected && (
                              <Chip
                                size="small"
                                label="เลือกแล้ว"
                                color="success"
                              />
                            )}
                          </Box>
                        </Box>
                      }
                      secondary={
                        qr.createdAt
                          ? new Date(qr.createdAt).toLocaleString("th-TH")
                          : "-"
                      }
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        </DialogContent>
      </Dialog>

      {/* SCANNER */}
      <Dialog
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>สแกน QR</DialogTitle>
        <DialogContent>
          {scannerError && <Alert severity="error">{scannerError}</Alert>}
          <Box
            id={scannerRegionId}
            sx={{ height: 280, bgcolor: "black", borderRadius: 1 }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QrMatchingDialog;